import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useRepair from '../../../hooks/useRepair';
import Card from '../../common/Card';
import Button from '../../common/Button';
import FormInput from '../../common/FormInput';
import FormTextarea from '../../common/FormTextarea';
import FormDatePicker from '../../common/FormDatePicker';
import FormFileUpload from '../../common/FormFileUpload';
import Loading from '../../common/Loading';
import ErrorMessage from '../../common/ErrorMessage';
import { getCsrfToken } from '../../../utils/csrfUtils';
import './styles.css';

function RepairForm({ token, user, warrantyId, onSuccess }) {
  const navigate = useNavigate();
  
  // 使用自定義Hook
  const { createRepair, updateLoading, updateError } = useRepair(token);

  // 本地狀態
  const [formData, setFormData] = useState({
    status: 'Pending',
    technician: '',
    initialDiagnosis: '',
    repairDetails: '',
    repairCost: '',
    attachedPhotos: []
  });

  // 圖片預覽URLs
  const [previewUrls, setPreviewUrls] = useState([]);
  
  // 錯誤狀態
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // 處理輸入變化
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));

    // 清除此欄位的錯誤
    if (formErrors[name]) {
      setFormErrors(prevErrors => ({
        ...prevErrors,
        [name]: null
      }));
    }
  };

  // 處理文件上傳
  const handleFileUpload = (files) => {
    setFormData(prevData => ({
      ...prevData,
      attachedPhotos: [...prevData.attachedPhotos, ...files]
    }));
  };

  // 處理文件刪除
  const handleFileDelete = (index) => {
    setPreviewUrls(prevUrls => prevUrls.filter((_, i) => i !== index));
    setFormData(prevData => ({
      ...prevData,
      attachedPhotos: prevData.attachedPhotos.filter((_, i) => i !== index)
    }));
  };

  // 表單驗證
  const validateForm = () => {
    const errors = {};

    if (!formData.technician.trim()) {
      errors.technician = '請填寫技術員姓名';
    }

    if (!formData.initialDiagnosis.trim()) {
      errors.initialDiagnosis = '請填寫初步診斷';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 處理表單提交
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setUploadError(null);

    try {
      // 處理圖片上傳
      const photoUrls = [];
      
      if (formData.attachedPhotos.length > 0) {
        for (const photo of formData.attachedPhotos) {
          const formDataForUpload = new FormData();
          formDataForUpload.append('file', photo);
          const csrfToken = getCsrfToken();
          try {
            const uploadResponse = await fetch('/api/upload', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'X-CSRF-Token': csrfToken
              },
              body: formDataForUpload
            });

            if (!uploadResponse.ok) {
              const errorData = await uploadResponse.json();
              throw new Error(`上傳圖片失敗: ${errorData.error || uploadResponse.statusText}`);
            }

            const uploadData = await uploadResponse.json();
            
            // 優先使用 shopifyFileId，如果沒有則使用 url
            if (uploadData.shopifyFileId) {
              photoUrls.push(uploadData.shopifyFileId);
            } else if (uploadData.url) {
              photoUrls.push(uploadData.url);
            }
          } catch (uploadError) {
            console.error('上傳圖片時出錯:', uploadError);
            setUploadError(uploadError.message);
            setIsSubmitting(false);
            return;
          }
        }
      }

      // 提交修改過的數據
      const result = await createRepair(warrantyId, {
        status: formData.status,
        technician: formData.technician,
        initialDiagnosis: formData.initialDiagnosis, // 後端已處理富文本格式轉換，無需前端轉換
        repairDetails: formData.repairDetails,       // 後端已處理富文本格式轉換，無需前端轉換
        repairCost: formData.repairCost,
        attachedPhotos: photoUrls
      });

      if (result) {
        if (onSuccess) {
          onSuccess(result);
        } else {
          navigate(`/warranties/${warrantyId}`);
        }
      }
    } catch (error) {
      console.error('提交表單時出錯:', error);
      setUploadError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 取消表單
  const handleCancel = () => {
    if (onSuccess) {
      onSuccess(null);
    } else {
      navigate(`/warranties/${warrantyId}`);
    }
  };

  return (
    <div className="repair-form-container">
      <Card className="repair-form-card">
        <h2 className="repair-form-title">新增維修單</h2>

        {(updateError || uploadError) && (
          <ErrorMessage message={updateError || uploadError} />
        )}

        <form onSubmit={handleSubmit} className="repair-form">
          <div className="form-row">
            <FormInput
              label="技術員"
              name="technician"
              value={formData.technician}
              onChange={handleInputChange}
              error={formErrors.technician}
              required
            />
            <FormInput
              label="維修費用"
              name="repairCost"
              value={formData.repairCost}
              onChange={handleInputChange}
              placeholder="例如：HK$500"
            />
          </div>

          <div className="form-row">
            <div className="form-col">
              <label>狀態</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-select"
              >
                <option value="Pending">待處理</option>
                <option value="In Progress">處理中</option>
                <option value="Completed">已完成</option>
                <option value="Cancelled">已取消</option>
              </select>
            </div>
          </div>

          <FormTextarea
            label="初步診斷"
            name="initialDiagnosis"
            value={formData.initialDiagnosis}
            onChange={handleInputChange}
            error={formErrors.initialDiagnosis}
            required
            rows={4}
          />

          <FormTextarea
            label="維修詳情"
            name="repairDetails"
            value={formData.repairDetails}
            onChange={handleInputChange}
            rows={4}
          />

          <FormFileUpload
            label="附加照片 (選填)"
            onUpload={handleFileUpload}
            onDelete={handleFileDelete}
            files={formData.attachedPhotos}
            multiple
            accept="image/*"
            token={token}
            previewUrls={previewUrls}
          />

          <div className="form-actions">
            <Button
              type="button"
              onClick={handleCancel}
              className="cancel-btn"
              disabled={isSubmitting || updateLoading}
            >
              取消
            </Button>
            <Button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting || updateLoading}
            >
              {isSubmitting || updateLoading ? '提交中...' : '提交'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

export default RepairForm; 